import { ReactNode, cloneElement, isValidElement, useContext, useMemo, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';

import { CheckboxGroup } from '@components/checkbox-group';
import { CopyrightFooter } from '@components/copyright-footer/copyright-footer';
import { FormLabel } from '@components/form-label';
import { createForm } from '@components/form/create-form';
import { DynamicFields, FieldWrapper, FormV2 } from '@components/form/v2';
import { FieldType } from '@components/form/v2';
import { IconButton } from '@components/icon-button';
import { IconReplace } from '@components/icons/replace';
import { IconTrash } from '@components/icons/trash';
import { RadioGroup } from '@components/radio-group';
import { Stack } from '@components/stack';
import { FieldMappingTextEditor } from '@components/text-editors';
import {
  FieldMappingEditorValue,
  mapVariableGroups,
} from '@components/text-editors/addons/field-mapping';
import { noop } from '@utils/browser';
import { pxToRem } from '@utils/converters';

import { FormBuilderContext } from './context';
import {
  ChoiceGroupWidthFix,
  DefaultValueMappingDisplay,
  MAPPED_OPTION_VALUE,
  fieldKindToMappingType,
  filterVariableGroupsByType,
  hasMappedDefault as checkMappedDefault,
  useMappedDefaultPill,
} from './default-value-mapping';
import { ContentFileFallback, MappedFileCard } from './fields/content-file';
import { ViewportType } from './state';
import { ViewportPreview } from './viewport-preview';

const Form = createForm();

const fieldLabel = (field: FieldType): string | undefined =>
  'label' in field && typeof field.label === 'string' ? field.label : undefined;

const fieldMappedDefault = (field: FieldType) =>
  'mappedDefaultValue' in field ? field.mappedDefaultValue : undefined;

type PreviewFieldHandler = (
  field: FieldType,
  children: ReactNode,
  hasMappedDefault: boolean,
) => ReactNode | null;

const mappedOnly =
  (fn: (field: FieldType, children: ReactNode) => ReactNode): PreviewFieldHandler =>
  (field, children, hasMapped) =>
    hasMapped ? fn(field, children) : null;

const clearableOverlay: PreviewFieldHandler = (field, children, hasMapped) =>
  hasMapped ? <PreviewClearableOverlay field={field}>{children}</PreviewClearableOverlay> : null;

const previewFieldHandlers: Partial<Record<string, PreviewFieldHandler>> = {
  'input-text-small': mappedOnly((field) => <PreviewTextFieldEditor field={field} />),
  'input-text-large': mappedOnly((field) => <PreviewTextFieldEditor field={field} />),
  'input-number': clearableOverlay,
  'input-email': clearableOverlay,
  'input-kennitala': clearableOverlay,
  'input-date': clearableOverlay,
  'input-phone': mappedOnly((field, children) => (
    <PreviewPhoneField field={field}>{children}</PreviewPhoneField>
  )),
  'input-file': mappedOnly((field, children) => (
    <PreviewFileField field={field}>{children}</PreviewFileField>
  )),
  'content-file': (field, children, hasMapped) => {
    if (hasMapped) {
      return (
        <MappedFileCard
          label={fieldLabel(field)}
          mappedDefaultValue={fieldMappedDefault(field)}
          isPreview
        />
      );
    }
    const url = 'url' in field && typeof field.url === 'string' ? field.url : '';
    return url ? children : <ContentFileFallback />;
  },
  'input-dropdown': clearableOverlay,
  'input-single-choice': mappedOnly((field) => <PreviewSingleChoiceField field={field} />),
  'input-multiple-choice': mappedOnly((field) => <PreviewMultipleChoiceField field={field} />),
};

const PreviewClearableOverlay = ({
  field,
  children,
  overlayLeft,
}: {
  field: FieldType;
  children: ReactNode;
  overlayLeft?: string;
}) => {
  const [cleared, setCleared] = useState(false);
  // Click anywhere on the underlying field (not the pill itself) → user is
  // overriding the mapped default, so hide the pill. We toggle `cleared` via
  // the `isHidden` prop instead of remounting, otherwise stateful children
  // like the select dropdown drop their selection.
  const handleClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    if (cleared) return;
    if (!(event.target as HTMLElement).closest('.node-wrapper')) {
      setCleared(true);
    }
  };
  return (
    <div onClickCapture={handleClickCapture}>
      <DefaultValueMappingDisplay
        field={field}
        onClear={() => setCleared(true)}
        overlayLeft={overlayLeft}
        isHidden={cleared}
      >
        {children}
      </DefaultValueMappingDisplay>
    </div>
  );
};

const defaultValueFieldWrapper: FieldWrapper = (field, children) => {
  const mappedDefault = fieldMappedDefault(field);
  const handler = previewFieldHandlers[field.kind];
  return handler?.(field, children, checkMappedDefault(mappedDefault)) ?? children;
};

const useMappedPreviewField = (field: FieldType) => ({
  pill: useMappedDefaultPill(field),
  options: 'options' in field ? (field.options ?? []) : [],
  label: fieldLabel(field) ?? '',
});

const PreviewSingleChoiceField = ({ field }: { field: FieldType }) => {
  const { pill, options, label } = useMappedPreviewField(field);
  return (
    <ChoiceGroupWidthFix>
      <RadioGroup label={label} defaultValue={MAPPED_OPTION_VALUE}>
        {[
          <RadioGroup.Option
            key={MAPPED_OPTION_VALUE}
            value={MAPPED_OPTION_VALUE}
            label={pill ?? ''}
          />,
          ...options.map((option) => (
            <RadioGroup.Option key={option.value} value={option.value} label={option.label} />
          )),
        ]}
      </RadioGroup>
    </ChoiceGroupWidthFix>
  );
};

const PreviewMultipleChoiceField = ({ field }: { field: FieldType }) => {
  const { pill, options, label } = useMappedPreviewField(field);
  return (
    <ChoiceGroupWidthFix>
      <CheckboxGroup label={label} defaultValue={[MAPPED_OPTION_VALUE]}>
        {[
          <CheckboxGroup.Item
            key={MAPPED_OPTION_VALUE}
            value={MAPPED_OPTION_VALUE}
            label={pill ?? ''}
          />,
          ...options.map((option) => (
            <CheckboxGroup.Item key={option.value} value={option.value} label={option.label} />
          )),
        ]}
      </CheckboxGroup>
    </ChoiceGroupWidthFix>
  );
};

export const PreviewFields = ({
  viewport,
  fields,
}: {
  viewport: ViewportType;
  fields: FormV2['fields'];
}) => {
  return (
    <ViewportPreview viewport={viewport}>
      <PreviewContainer>
        <Form onSubmit={noop}>
          <AnimatePresence>
            <motion.div layout="position" data-mobile={viewport === 'mobile'}>
              <DynamicFields fields={fields} preview fieldWrapper={defaultValueFieldWrapper} />
            </motion.div>
          </AnimatePresence>
          <CopyrightFooter marginTop={16} />
        </Form>
      </PreviewContainer>
    </ViewportPreview>
  );
};

const PreviewTextFieldEditor = ({ field }: { field: FieldType }) => {
  const variableGroups = useContext(FormBuilderContext)?.variableGroups;
  const fieldType = fieldKindToMappingType[field.kind] ?? 'text';

  const filteredGroups = useMemo(() => {
    if (!variableGroups?.length) return [];
    return filterVariableGroupsByType(variableGroups, fieldType);
  }, [variableGroups, fieldType]);

  const mappedDefault = fieldMappedDefault(field);

  const initialValue = useMemo((): FieldMappingEditorValue => {
    if (!mappedDefault || typeof mappedDefault === 'string') {
      return { value: '', variables: mapVariableGroups(filteredGroups) };
    }
    return {
      ...mappedDefault,
      variables: { ...mapVariableGroups(filteredGroups), ...mappedDefault.variables },
    };
  }, [mappedDefault, filteredGroups]);

  const isSingleLine = field.kind !== 'input-text-large';

  return (
    <FieldMappingTextEditor
      defaultValue={initialValue}
      variableGroups={filteredGroups}
      fieldMappingId={`${field.id}-default`}
      fieldType={fieldType}
      plaintext={isSingleLine}
      singleLine={isSingleLine}
      label={fieldLabel(field)}
      key={`preview-mapping-${field.kind}-${field.id}`}
      actions={<></>}
      disableExpandable
    />
  );
};

const PHONE_NUMBER_OVERLAY_LEFT = '8.5rem';

const PreviewPhoneField = ({ field, children }: { field: FieldType; children: ReactNode }) => {
  const mappedDefault = fieldMappedDefault(field);
  const hasVariableMapping =
    mappedDefault &&
    typeof mappedDefault !== 'string' &&
    mappedDefault.value &&
    mappedDefault.value.includes('{{');

  if (hasVariableMapping) {
    return (
      <PreviewClearableOverlay field={field} overlayLeft={PHONE_NUMBER_OVERLAY_LEFT}>
        {children}
      </PreviewClearableOverlay>
    );
  }

  if (checkMappedDefault(mappedDefault) && isValidElement(children)) {
    return cloneElement(children as React.ReactElement<any>, {
      defaultValue: mappedDefault.value,
    });
  }

  return <>{children}</>;
};

const PreviewFileField = ({ field, children }: { field: FieldType; children: ReactNode }) => {
  const mappedDefault = fieldMappedDefault(field);
  const variables = mappedDefault?.variables ?? {};
  const variableOccurrences = mappedDefault?.value?.match(/\{\{\s*([^}]+?)\s*\}\}/g) ?? [];
  const label = fieldLabel(field);
  const allowMultiple = 'allowMultiple' in field && field.allowMultiple;
  const [hiddenIndexes, setHiddenIndexes] = useState<Set<number>>(new Set());

  const visibleCount = variableOccurrences.length - hiddenIndexes.size;
  const showUploadArea = allowMultiple || visibleCount === 0;
  const hideRow = (index: number) => setHiddenIndexes((prev) => new Set(prev).add(index));

  return (
    <Stack space={1}>
      <FormLabel text={label} />
      <Stack space={2}>
        {variableOccurrences.map((match, index) => {
          if (hiddenIndexes.has(index)) return null;
          const key = match.replace(/\{\{\s*|\s*\}\}/g, '');
          return (
            <MappedFileCard
              key={`${key}-${index}`}
              mappedDefaultValue={{ value: match, variables }}
              isPreview
              dataTestId="mapped-input-file-card"
              extraAction={
                <IconButton
                  variant="ghost-purple"
                  icon={allowMultiple ? <IconTrash /> : <IconReplace />}
                  onClick={() => hideRow(index)}
                />
              }
            />
          );
        })}
        {showUploadArea && <HideLabel>{children}</HideLabel>}
      </Stack>
    </Stack>
  );
};

const HideLabel = styled.div({
  '& label': { display: 'none' },
});

const PreviewContainer = styled.div({
  width: '100%',
  maxWidth: pxToRem(535),

  '[data-viewport="desktop"] &': {
    maxWidth: pxToRem(465),
  },
});
